import React, { memo, useState } from "react";

import { LoadingPage } from "@hl/base-components/lib/LoadingPage";
import { RightCaret } from "@hl/base-components/lib/assets/icons.generated";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import ExternalCollectionLink from "@hl/shared-features/lib/features/eth/ExternalCollectionLink";
import { AppSEO } from "@hl/shared-features/lib/features/layout/AppSEO";
import { networkLookup } from "@hl/shared-features/lib/utils/blockExplorer";
import { Group, Text, Stack, Title, Space, Divider } from "@mantine/core";
import { Link } from "react-router-dom";

import { Contract2 } from "apollo/graphql.generated";
import { TOOLS_URL } from "config";
import { useGetContractsByUserQuery } from "~/features/collections/queries/contracts.graphql.generated";
import CreateFirstCollection from "~/features/dashboard/CreateFirstCollection";
import DashboardContent from "~/features/dashboard/DashboardContent";

import CustomPaginationWrapper from "../../../tokens/components/CustomPaginationWrapper";
import { monthDayYearStr } from "../../forms/CreateCollectionMultistepForm/timeUtil";

interface ContractTitleProps {
  contract: Contract2;
}

const ContractTitle = ({ contract }: ContractTitleProps) => {
  const { address, name, contractType, chainId } = contract;
  const networkData = networkLookup(chainId);

  return (
    <Group noWrap position="apart">
      <Stack spacing={4}>
        <Space h="sm" />
        <Title order={5}>{name}</Title>
        <Text size="sm" color={TEXT_COLOR.SECONDARY}>
          <Group spacing={4}>
            <span>{networkData.displayName}</span>
            <span>·</span>
            <span>{contractType}</span>
            <span>·</span>
            {address && (
              <ExternalCollectionLink
                chainId={chainId}
                address={address}
                anchorAddress={true}
                addressType={"contract"}
              />
            )}
          </Group>
        </Text>
        <Text size="sm" color={TEXT_COLOR.SECONDARY}>
          {!contract?.createdAt ? (
            <br />
          ) : (
            `Created on ${monthDayYearStr(new Date(contract.createdAt))}`
          )}
        </Text>
        <Space h="sm" />
      </Stack>
      <Stack align="center">
        <RightCaret />
      </Stack>
    </Group>
  );
};

const ContractsDashboardV2 = () => {
  const { data: queryData, loading } = useGetContractsByUserQuery({
    //TODO: Refresh query on create contract
    fetchPolicy: "network-only",
  });

  const [activePage, setPage] = useState(1);
  const [lowerBound, setLowerBound] = useState(1);
  const [upperBound, setUpperBound] = useState(1);

  // temp fix to sort in desc create order
  const contracts = [...(queryData?.getContractsByUser ?? [])].reverse();

  return (
    <DashboardContent>
      <AppSEO title="Contracts" />
      {loading ? (
        <LoadingPage />
      ) : contracts.length === 0 ? (
        <CreateFirstCollection />
      ) : (
        <CustomPaginationWrapper
          itemsPerPage={10}
          activePage={activePage}
          setPage={setPage}
          setLowerBound={setLowerBound}
          setUpperBound={setUpperBound}
          maxLength={contracts.length ?? 1}
        >
          {contracts.slice(lowerBound - 1, upperBound).map((contract, i) => (
            <div key={i}>
              <Text
                component={Link}
                to={TOOLS_URL.contract.detail.replace(
                  ":contractId",
                  contract.id
                )}
                target="_blank"
              >
                <ContractTitle contract={contract} />
              </Text>
              {i < upperBound - lowerBound && <Divider />}
            </div>
          ))}
        </CustomPaginationWrapper>
      )}
    </DashboardContent>
  );
};

export default memo(ContractsDashboardV2);
